import React from 'react';
import { Attributes, AttributeValueEditors, ItemModel, Details } from '@leverege/ui-attributes';
import { Flex, Label } from '@leverege/ui-elements';
import { GlobalState } from '@leverege/ui-redux';
import P from '@leverege/promise';
import { Plugins, Match } from '@leverege/plugin';
import { ValuesCache } from '@leverege/value-cache';
import { flatten } from '@leverege/object-util';
import StaticValueDetailModel from "./staticImageDetail/StaticImageDetailModel.js";
const CACHES = new ValuesCache();
const createMatch = (path, objectType, actionType) => {
  return {
    path,
    objectType,
    actionType
  };
};
function numSortComparator(a, b) {
  const an = a.sort || 0;
  const bn = b.sort || 0;
  return an - bn;
}
export default {
  getPairingDelegate(path, objectType, actionType, context) {
    return CACHES.calc('pairingDelegates', (path, objectType, actionType) => {
      const matches = createMatch(path, objectType, actionType);
      const v = Plugins.createView('PairingDelegateGetter', {
        name: `${path}.${objectType}`,
        filter: plg => Match.isMatch(matches, plg.matches)
      });
      const plgs = v.get().sort(numSortComparator);
      if (plgs && plgs.length > 0) {
        return plgs[0].getDelegate(context);
      }
      return null;
    }, path, objectType, actionType, Plugins.getVersion('PairingDelegateGetter'));
  },
  getSelectionRenderer(path, objectType, actionType) {
    return CACHES.calc('selectionRenderers', (path, objectType, actionType) => {
      const matches = createMatch(path, objectType, actionType);
      const v = Plugins.createView('PairingSelectionRenderer', {
        name: `${path}.${objectType}.${actionType}`,
        filter: plg => Match.isMatch(matches, plg.matches)
      });
      const plgs = v.get().sort(numSortComparator);
      if (plgs && plgs.length > 0) {
        return plgs[0].component;
      }
      return null;
    }, path, objectType, actionType, Plugins.getVersion('PairingSelectionRenderer'));
  },
  getEditableAttributes(metadata, objectType, opts) {
    if (!metadata?.extraFields) {
      return null;
    }
    const objType = objectType?.objectType || objectType;
    const {
      requiredOnly = false
    } = opts || {};
    return metadata.extraFields.map(attr => {
      const {
        name,
        required
      } = attr;
      return requiredOnly && required ? Attributes.getAttribute(name, objType) : requiredOnly ? null : Attributes.getAttribute(name, objType);
    }).filter(val => val != null);
  },
  getAttributeEditors(metadata, cxt) {
    if (!metadata?.extraFields) {
      return null;
    }
    const {
      delegate,
      device,
      deviceFormData,
      disabled,
      onChange,
      relationship,
      type
    } = cxt;
    return metadata.extraFields.map(field => {
      const {
        name,
        showExistingValue,
        readOnly = false
      } = field;
      const attr = Attributes.getAttribute(name, relationship.objectType);
      let value = deviceFormData && attr.get(deviceFormData) ? attr.get(deviceFormData) : device && attr && showExistingValue ? attr.get(device) : null;
      if (value?.type === 'set' || value?.type === 'add' || value?.type === 'remove') {
        value = value.value[0];
      }
      const editor = readOnly ? null : AttributeValueEditors.getEditorFor(name, relationship.objectType)?.create({
        attribute: attr,
        value,
        onChange,
        eventData: `${type}/${attr?.source?.field}`,
        editable: true,
        object: device,
        disabled: disabled != null ? disabled : !device,
        allowDelete: false,
        context: {
          clientProps: {
            delegate
          }
        }
      });
      if (editor) {
        return /*#__PURE__*/React.createElement(Flex, {
          variant: "colS",
          key: name
        }, /*#__PURE__*/React.createElement(Label, {
          variant: "inputTitle"
        }, attr.displayName), editor);
      }
      return null;
    });
  },
  getSearchFieldsFromAttributes(attributes, objectType) {
    const fields = attributes.map(attr => {
      const uiAttr = Attributes.getAttribute(attr, objectType);
      return uiAttr?.source?.field;
    }).filter(attr => attr != null);
    if (fields && fields.length === 0) {
      return null;
    }
    return fields;
  },
  getItemModelFromAttributes(attributes, context) {
    const {
      objectType
    } = context;
    // eslint-disable-next-line prefer-const
    let model = ItemModel.create();
    const details = attributes?.reduce((prev, attr) => {
      const attribute = Attributes.getAttribute(attr.name, objectType);
      const detail = Details.getDetail(attr.name, objectType);
      if (attribute.valueType === 'resourceImage') {
        prev.push(StaticValueDetailModel.create(attr.name, objectType));
      } else if (detail) {
        prev.push(detail.create());
      }
      return prev;
    }, []);
    if (!details) {
      return null;
    }
    model = ItemModel.addType(model, objectType, {
      details
    });
    return model;
  },
  async performDeviceUpdates(deviceData, delegate, {
    extraFields,
    objectType
  }) {
    if (!extraFields) {
      return;
    }
    const {
      data,
      resources
    } = extraFields.reduce((prev, field) => {
      if (field.readOnly) {
        return prev;
      }
      const attribute = Attributes.getAttribute(field.name, objectType);
      if (!attribute) {
        console.warn(`No attribute ${field.name} exists on ${objectType}`);
        return prev;
      }
      const value = attribute.get({
        data: deviceData
      });
      if (value?.type === 'set' || value?.type === 'remove') {
        prev.resources.push({
          path: attribute.source?.updatePath?.replace('data/', ''),
          value
        });
      } else {
        prev.data.push({
          path: attribute.source?.updatePath?.replace('data/', ''),
          value
        });
      }
      return prev;
    }, {
      data: [],
      resources: []
    });
    if (data) {
      await GlobalState.dispatch(delegate.update({
        data
      }, {
        resolveOnWrite: true,
        refresh: 'wait_for'
      }));
    }
    if (resources) {
      await P.map(resources, async resource => {
        const {
          path,
          value: op
        } = resource;
        const {
          value
        } = op;
        const resourceDelegate = delegate.resource(`resourceImage/${path}`);
        await GlobalState.dispatch(resourceDelegate.create(value[0], {
          type: value[0].type
        })).catch(err => {
          if (err.status === 500) {
            GlobalState.dispatch(resourceDelegate.update([{
              path: 'type',
              value: value[0].type
            }], value[0]));
          } else {
            throw err;
          }
        });
        return GlobalState.dispatch(delegate.update({
          data: {
            [path]: value[0].name
          }
        }));
      });
    }
  }
};